import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Search, Person } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(() => ({
  dialogContent: {
    minWidth: 360,
    minHeight: 300,
    padding: 0,
  },
  searchBox: {
    padding: "8px 16px",
  },
  list: {
    maxHeight: 320,
    overflowY: "auto",
  },
  listItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
    "&.selected": {
      backgroundColor: "rgba(0,120,212,0.08)",
    },
  },
  emptyText: {
    padding: 16,
    textAlign: "center",
    color: "gray",
  },
}));

const ForwardMessageModal = ({ open, onClose, messageId }) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [forwarding, setForwarding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelected(null);
    setLoading(true);
    api
      .get("/tickets", { params: { status: "open", pageNumber: 1 } })
      .then(({ data }) => setTickets(data.tickets || []))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = tickets.filter((t) => {
    const name = t.contact?.name || "";
    const number = t.contact?.number || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      number.includes(search)
    );
  });

  const handleForward = async () => {
    if (!selected) return;
    setForwarding(true);
    try {
      await api.post(`/messages/${messageId}/forward`, {
        targetTicketId: selected.id,
      });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setForwarding(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Encaminhar mensagem</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.searchBox}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar contato ou número..."
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <Typography className={classes.emptyText}>
            Nenhum atendimento aberto encontrado.
          </Typography>
        ) : (
          <List className={classes.list} disablePadding>
            {filtered.map((ticket) => (
              <ListItem
                key={ticket.id}
                className={`${classes.listItem} ${selected?.id === ticket.id ? "selected" : ""}`}
                onClick={() => setSelected(ticket)}
                divider
              >
                <ListItemAvatar>
                  <Avatar src={ticket.contact?.profilePicUrl}>
                    <Person />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={ticket.contact?.name || ticket.contact?.number}
                  secondary={`#${ticket.id} • ${ticket.contact?.number}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={forwarding}>
          Cancelar
        </Button>
        <Button
          onClick={handleForward}
          color="primary"
          variant="contained"
          disabled={!selected || forwarding}
        >
          {forwarding ? <CircularProgress size={20} /> : "Encaminhar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardMessageModal;
